import React, { forwardRef } from 'react'
import { Box, Typography, Card, CardContent } from '@mui/material'
import LockIcon from '@mui/icons-material/Lock'
import { PatientNotesSectionForReports } from '../PrescriptionMakingPageComponents'

/**
 * Modern Private Notes Section Component
 * Wraps PatientNotesSectionForReports with modern card design
 * Similar to Therapy Report Making Page implementation
 */
const PrivateNotesSection = forwardRef(
  ({ patientName, thisBookedEventId, patientId }, ref) => {
    // Access the internal state from PatientNotesSectionForReports via ref
    // For now, we'll hide the header and let PatientNotesSectionForReports handle its own state
    return (
      <Card
        sx={{
          mb: 3,
          maxWidth: '1200px',
          width: '100%',
          backgroundColor: 'rgba(249, 250, 251, 0.4)',
          border: '1px dashed rgba(139, 92, 246, 0.25)',
          borderRadius: '16px',
          boxShadow: '0 2px 8px rgba(139, 92, 246, 0.08)',
          overflow: 'visible',
          position: 'relative',
          marginTop: '48px',
          paddingTop: '48px',
          borderTop: '2px solid rgba(156, 163, 175, 0.3)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, rgba(156, 163, 175, 0.4) 0%, rgba(107, 114, 128, 0.4) 100%)',
            borderRadius: '16px 16px 0 0',
          },
        }}
        className="hide-on-print"
      >
        <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
          {/* Header Section */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              mb: 2.5,
              pb: 1.5,
              borderBottom: '2px dashed rgba(156, 163, 175, 0.4)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
              <Box
                sx={{
                  width: { xs: '32px', md: '36px' },
                  height: { xs: '32px', md: '36px' },
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(135deg, rgba(156, 163, 175, 0.15) 0%, rgba(107, 114, 128, 0.15) 100%)',
                  color: '#6b7280',
                  flexShrink: 0,
                }}
              >
                <LockIcon sx={{ fontSize: { xs: 18, md: 20 } }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: { xs: '18px', md: '20px' },
                    fontWeight: 700,
                    color: '#6b7280',
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                    letterSpacing: '-0.01em',
                    mb: 0.5,
                  }}
                >
                  Private Notes
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '12px',
                      fontWeight: 500,
                      color: '#9ca3af',
                      marginLeft: '8px',
                      fontStyle: 'italic',
                    }}
                  >
                    (Private - Not part of prescription)
                  </Typography>
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#9ca3af',
                    fontSize: '12px',
                    fontStyle: 'italic',
                    display: { xs: 'none', sm: 'block' },
                  }}
                >
                  These notes are completely private and only visible to you
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Private Notes Content */}
          <Box sx={{ mt: 2 }}>
            <PatientNotesSectionForReports
              ref={ref}
              patientName={patientName}
              thisBookedEventId={thisBookedEventId}
              patientId={patientId}
              hideHeader={true}
            />
          </Box>
        </CardContent>
      </Card>
    )
  },
)

PrivateNotesSection.displayName = 'PrivateNotesSection'

export default PrivateNotesSection

